import {
  exportLegacyMindmap,
  importLegacyMindmap,
  MindmapImportError,
  mindmapToMarkdown,
  type MindmapNode,
} from '@blueprint/domain'

/** Mindmap import and export. Everything runs in the browser; there is no export endpoint. */

export function fileSlug(name: string, fallback = 'mindmap'): string {
  return name.replace(/[^\w.-]+/g, '-').replace(/^-+|-+$/g, '').toLowerCase() || fallback
}

export function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  // Revoked on the next tick: some browsers start the download after the click returns.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/** The legacy app's file format, so the file opens there and imports back here. */
export function exportJson(node: MindmapNode, name: string): void {
  const json = JSON.stringify(exportLegacyMindmap(node), null, 2)
  download(new Blob([json], { type: 'application/json' }), `${fileSlug(name)}.json`)
}

export function exportMarkdown(node: MindmapNode, name: string): void {
  download(new Blob([mindmapToMarkdown(node)], { type: 'text/markdown' }), `${fileSlug(name)}.md`)
}

export interface ImportedMindmap {
  fileName: string
  /** The map's name: the root node's text. */
  name: string
  root: MindmapNode
}

/** Reads one legacy (or exported) `.json` file. Throws an error naming the file and the bad path. */
export async function readMindmapFile(file: File): Promise<ImportedMindmap> {
  let data: unknown
  try {
    data = JSON.parse(await file.text())
  } catch {
    throw new Error(`${file.name}: not a JSON file.`)
  }
  try {
    const root = importLegacyMindmap(data)
    return { fileName: file.name, name: root.title, root }
  } catch (error) {
    if (error instanceof MindmapImportError) throw new Error(`${file.name}: ${error.message}`)
    throw error
  }
}
